import { HttpParams } from '@angular/common/http';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { FilterGroupComponent } from '@app/components/filter-group/filter-group.component';
import { ModalComponent } from '@app/components/info-modal/modal/modal.component';
import { DropDownButtonComponent } from '@app/components/input/drop-down-button/drop-down-button.component';
import { LoadingSpinnerComponent } from '@app/components/loading-spinner/loading-spinner.component';
import { MapComponent } from '@app/components/map/map.component';
import { MultiLinkAccordionComponent } from '@app/components/multi-link-accordion/multi-link-accordion.component';
import { PageErrorComponent } from '@app/components/page-error/page-error.component';
import { LegendItem } from '@app/models/data-types/map-layer-info';
import { DropDownButton } from '@app/models/elements/drop-down-button';
import { ContextualizedSelectsFilterConfig } from '@app/models/filters/contextualized-selects-filter-config';
import { ClassAmmFilterMapper, FilterMapper } from '@app/models/filters/filter-mapper';
import { FilterStatus } from '@app/models/filters/filter-status';
import * as FilterType from '@app/models/filters/filter-type';
import { GenericFilterConfig } from '@app/models/filters/generic-filter-config';
import { StaticLegendWmsLayer } from '@app/models/map/StaticLegendWmsLayer';
import { WmsLayer } from '@app/models/map/WmsLayer';
import { PageConfig } from '@app/models/page-config';
import { ApiCallerService } from '@app/services/api-caller.service';
import { RouterService } from '@app/services/router.service';
import saveAs from 'file-saver';
import html2canvas from 'html2canvas';
import * as jsPDF from 'jspdf';
import * as L from 'leaflet';
import * as hardcodedValues from './hardcoded';

@Component({
	selector: 'app-limiti-view',
	standalone: true,
	imports: [
		MultiLinkAccordionComponent,
		ModalComponent,
		DropDownButtonComponent,
		FilterGroupComponent,
		LoadingSpinnerComponent,
		MapComponent,
		PageErrorComponent,
	],
	templateUrl: './limiti-view.component.html',
	styleUrl: './limiti-view.component.css',
})
export class LimitiViewComponent {
	contentLoaded: boolean = false;
	pageConfig: PageConfig = {} as PageConfig;
	filtersStatus: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } } = {};
	visibleFilters: GenericFilterConfig[] = [];
	map: L.Map = {} as L.Map;

	filterMapper: FilterMapper = new ClassAmmFilterMapper();

	downloadDataDropdown: DropDownButton = {
		text: 'DOWNLOAD DATI',
		droppedItems: [
			{
				text: 'VISUALIZZA',
				onClick: () => this.visualizeReport(),
				iconName: 'it-password-visible',
				highlight: true,
			},
			{
				text: 'SCARICA PDF',
				onClick: () => this.downloadPdfReport(),
				iconName: 'it-file-pdf-ext',
				highlight: false,
			},
			{
				text: 'SCARICA EXCEL',
				onClick: () => this.downloadXlsReport(),
				iconName: 'it-file-sheet',
				highlight: false,
			},
		],
	};

	downloadMapDropdown: DropDownButton = {
		text: 'DOWNLOAD MAPPA',
		droppedItems: [
			{
				text: 'SCARICA PDF',
				onClick: () => this.exportMap('pdf'),
				iconName: 'it-file-pdf-ext',
				highlight: false,
			},
			{
				text: 'SCARICA IMMAGINE',
				onClick: () => this.exportMap('png'),
				iconName: 'it-camera',
				highlight: false,
			},
		],
	};

	showDownloadDataLoadingSpinner = false;
	showDownloadMapLoadingSpinner = false;
	showReportError: boolean = false;
	showPageError: boolean = false;

	layersInfo = hardcodedValues.layersInfo;
	layers: WmsLayer[] = [];

	constructor(
		private router: Router,
		private apiCallerService: ApiCallerService,
		private routerService: RouterService,
	) {
		for (let li of this.layersInfo) {
			let layer = new StaticLegendWmsLayer(
				li.name,
				li.visibleFiltersIds,
				li.modal,
				li.themes,
				li.selected,
				li.legend,
			);
			this.layers.push(layer);
		}
	}

	ngOnInit() {
		this.routerService.setActivePage(this.router.url);
		this.apiCallerService.getLimitiPageConfig(
			(data: PageConfig) => {
				this.pageConfig = data;
				this.initFilterStatus();
				this.setVisibleFilters(this.layers);
			}, // next
			() => {
				this.showPageError = true;
			}, //error
			() => (this.contentLoaded = true), // complete
		);
	}

	/**
	 * Stores reference to the map as soon as it loads. Passed as Output to map component.
	 * @param map
	 */
	onMapLoad = (map: L.Map) => {
		this.map = map;
	};

	/**
	 * Updates the filters status map with the new status received from the filter that changed.
	 * @param newStatus
	 */
	setNewFilterStatus = (newStatus: FilterStatus) => {
		this.filtersStatus = {
			...this.filtersStatus,
			[newStatus.filterId]: {
				selectedPrimaryValues: newStatus.selectedPrimaryValues,
				selectedSecondaryValues: newStatus.selectedSecondaryValues,
			},
		};

		console.log('LIMITI.setNewFilterStatus: ', this.filtersStatus);
	};

	/**
	 * Sets selected layer in list of layerInfo,
	 *Ssets visible filters based on selected layer
	 * @param selectedLayerName
	 */
	reactToLayerSelection = (selectedLayerName: string) => {
		this.setSelectedLayer(selectedLayerName);
		this.setVisibleFilters(this.layers);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, opens a new window with pdf report.
	 */
	visualizeReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'pdf');
		this.showDownloadDataLoadingSpinner = true;

		this.apiCallerService.getLimitiReport(
			params,
			(data: Blob) => {
				this.showDownloadDataLoadingSpinner = false;
				this.openNewWindow(data);
			},
			() => {
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, report download as PDF is started.
	 */
	downloadPdfReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'pdf');

		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getLimitiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadDataLoadingSpinner = false;
				saveAs(data, 'Trasporti Eccezionali.pdf');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, report download as XLS is started.
	 */
	downloadXlsReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'xls');

		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getLimitiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadDataLoadingSpinner = false;
				saveAs(data, 'Trasporti Eccezionali.xls');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Export map as pdf or png
	 * @param format string representing the format: 'pdf' or 'png'
	 */
	exportMap = (format: string) => {
		this.showDownloadMapLoadingSpinner = true;

		let mapContainer = document.getElementsByClassName('leaflet-container')[0] as HTMLElement;
		document.querySelector('.leaflet-control-container')?.classList.add('d-none');

		html2canvas(mapContainer, {
			allowTaint: true,
			useCORS: true,
			scale: 1,
		}).then((canvas) => {
			let img = {
				dataUrl: canvas.toDataURL('image/png'),
				dimensions: {
					width: canvas.width,
					height: canvas.height,
				},
			};

			if (format === 'png') {
				var link = document.createElement('a');
				link.download = 'esportazione.png';
				link.href = img.dataUrl;
				link.click();
			} else {
				let pdf = this.buildPdf(img);

				pdf.save('esportazione.pdf');
			}

			document.querySelector('.leaflet-control-container')?.classList.remove('d-none');
			this.showDownloadMapLoadingSpinner = false;
		});
	};

	private buildPdf = (img: any): jsPDF.jsPDF => {
		var maxSheetWidth = 297;
		var maxSheetHeight = 210;

		var maxImageWidth = 295;
		var maxImageHeight = 130;

		var imgAspectRatio = img.dimensions.width / img.dimensions.height;

		var resizedImageWidth = 200; //dummy
		var resizedImageHeight = 200; //dummy

		if (imgAspectRatio > 1) {
			// Larghezza pi? grande dell'altezza (immagine in orizzontale)
			resizedImageWidth = maxImageWidth;
			resizedImageHeight = resizedImageWidth / imgAspectRatio;

			if (resizedImageHeight > maxImageHeight) {
				resizedImageHeight = maxImageHeight;
				resizedImageWidth = resizedImageHeight * imgAspectRatio;
			}
		} else {
			//Larghezza più piccola dell'altezza (immagine in verticale)
			resizedImageHeight = maxImageHeight;
			resizedImageWidth = resizedImageHeight * imgAspectRatio;

			if (resizedImageWidth > maxImageWidth) {
				resizedImageWidth = maxImageWidth;
				resizedImageHeight = resizedImageWidth / imgAspectRatio;
			}
		}

		var imageHorizontalMargin = (maxSheetWidth - resizedImageWidth) / 2;
		var imageVerticalMargin = 20;

		var selectedLayer = this.layers.find((l) => l.selected)!;

		var pdf = new jsPDF.jsPDF('l', 'mm', 'a4');

		var lastPdfY = imageVerticalMargin;

		// add map image
		pdf.addImage(img.dataUrl, 'png', imageHorizontalMargin, lastPdfY, resizedImageWidth, resizedImageHeight);

		lastPdfY = lastPdfY + resizedImageHeight + 10;

		// add selected layer
		pdf.setFontSize(10);
		let selectedLayerText = `Livello attivo: ${selectedLayer.name}`;
		pdf.text(selectedLayerText, 10, lastPdfY);

		// add logo rer
		var logoRERImg = document.createElement('img');
		logoRERImg.src = `./assets/images/logo-rer.jpg`;
		pdf.addImage(logoRERImg, 'JPEG', 247, 7, 40, 8.6);

		lastPdfY = lastPdfY + 10;

		// add color legend
		var legendElements = selectedLayer.legend;

		var lineeDisegnate = 0;
		var larghezzaSingoloElementoLegenda = 70;
		var currentOffsetY = lastPdfY;
		var columnHeight = lastPdfY + 40;
		var columns = 0;

		legendElements.forEach((el: LegendItem, i: number) => {
			var legendLabel = el.label;
			var offsetX = 10 + columns * larghezzaSingoloElementoLegenda;

			// legend is colored circles
			if (el.colorHex) {
				pdf.setFillColor(el.colorHex);
				pdf.circle(offsetX, currentOffsetY, 3, 'F');
				pdf.text(legendLabel, offsetX + 4, currentOffsetY + 1);
			} else {
				// legend is icons (limiti puntuali)
				larghezzaSingoloElementoLegenda = 40;
				var legendIconElement = document.createElement('img');
				legendIconElement.src = el.iconPath!;
				pdf.addImage(legendIconElement, 'png', offsetX, currentOffsetY, 3, 3);
				pdf.text(legendLabel, offsetX + 6, currentOffsetY + 4);
			}

			if (currentOffsetY + 10 >= columnHeight) {
				columns += 1;
				currentOffsetY = lastPdfY;
			} else {
				currentOffsetY += 10;
			}
			lineeDisegnate++;
			lineeDisegnate++;
		});

		// add link
		pdf.setFontSize(8);
		pdf.text(location.href, maxSheetWidth - 100, maxSheetHeight - 7);

		// add date
		var dataAttuale = new Date();
		var dataAttualeAsString = dataAttuale.toLocaleDateString();
		var tempoAttualeAsString = dataAttuale.toLocaleTimeString();
		pdf.text(dataAttualeAsString + ' ' + tempoAttualeAsString, maxSheetWidth - 60, maxSheetHeight - 11);

		return pdf;
	};

	/**
	 * Sets selected attribute of layerInfo by layer name
	 * @param selectedLayerName
	 */
	private setSelectedLayer(selectedLayerName: string) {
		this.layers.forEach((l) => {
			if (l.name === selectedLayerName) {
				l.selected = true;
			} else {
				l.selected = false;
			}
		});

		this.layers = [...this.layers];
	}

	/**
	 * Sets visible filters.
	 * Updates the default values with the currently selected ones in the filtersStatus.
	 * This is done so that when we switch between layers, the selected filters values are kept.
	 * @param visibleFiltersIds
	 */
	private setVisibleFilters = (layers: WmsLayer[]) => {
		let selectedLayerInfo = layers.find((l) => l.selected);
		let visibleFiltersIds = selectedLayerInfo?.visibleFilterIds;
		this.visibleFilters = [...this.pageConfig.filters.filter((f) => visibleFiltersIds?.includes(f.id))];

		// Update default values to match current values in filtersStatus
		// this is done to persist values of filters that at some point appeared/disappeared (as a consequence to layer selection)
		this.visibleFilters.forEach((vf) => {
			switch (vf.type) {
				case FilterType.CONTEXTUALIZED_SELECTS:
					let casted = vf as ContextualizedSelectsFilterConfig;
					casted.subfilters.first.defaultValues =
						this.filtersStatus[casted.subfilters.first.id].selectedPrimaryValues;
					casted.subfilters.second.defaultValues =
						this.filtersStatus[casted.subfilters.second.id].selectedPrimaryValues;
					break;
				default:
					vf.defaultValues = this.filtersStatus[vf.id].selectedPrimaryValues;
					break;
			}
		});
	};

	/**
	 * Initializes filtersStatus with filters default values
	 */
	private initFilterStatus = () => {
		for (const filter of this.pageConfig.filters) {
			switch (filter.type) {
				// for a contextualized filter, we need to init its children in the filtersStatus
				// we're not tracking the selected values at parent level
				case FilterType.CONTEXTUALIZED_SELECTS:
					let casted = filter as ContextualizedSelectsFilterConfig;
					this.filtersStatus[casted.subfilters.first.id] = {
						selectedSecondaryValues: casted.subfilters.first.defaultValues,
						selectedPrimaryValues: casted.subfilters.first.defaultValues,
					};

					this.filtersStatus[casted.subfilters.second.id] = {
						selectedSecondaryValues: casted.subfilters.second.defaultValues,
						selectedPrimaryValues: casted.subfilters.second.defaultValues,
					};
					break;
				default:
					this.filtersStatus[filter.id] = {
						selectedPrimaryValues: [...filter.defaultValues],
						selectedSecondaryValues: [...filter.defaultValues],
					};
					break;
			}
		}
	};

	/**
	 * Opens new window and displays content of passed Blob
	 * @param data
	 */
	private openNewWindow = (data: Blob) => {
		const url = window.URL.createObjectURL(data);
		window.open(url, '_blank', 'height=600, width=1000');
	};

	/**
	 * Prepares params for report generation
	 * @param reportType
	 * @param reportFormat 'pdf' or 'xls'
	 */
	private loadReportParams = (reportType: string, reportFormat: string) => {
		let params = new HttpParams();

		// restrict params to the ones required by the selected layer
		// each layer specifies an array of filter ids that are active for that layer
		// we get that list, then filter the filterStatus on the list
		let selectedLayerInfo = this.layers.find((l) => l.selected);
		let ids = Object.keys(this.filtersStatus).filter((k) => selectedLayerInfo?.visibleFilterIds.includes(k));
		for (const id of ids) {
			const values = this.filtersStatus[id].selectedPrimaryValues;

			for (const value of values) {
				params = params.append(id, value);
			}
		}

		params = params.append('reportType', reportType);
		params = params.append('reportFormat', reportFormat);

		return params;
	};
}
